import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import useTranslation from '@/hooks/use-translation';
import { useMode } from '@/contexts/ModeContext';
import { Loader2, Save, User } from 'lucide-react';

interface ProfileData {
  id: string;
  full_name: string;
  email: string;
  organization?: string;
  water_usage_goal?: number;
  avatar_url?: string;
}

const Profile = () => {
  const { user, updateUserProfile } = useAuth();
  const { toast } = useToast();
  const { translate } = useTranslation();
  const { mode } = useMode();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profileData, setProfileData] = useState<ProfileData>({
    id: '',
    full_name: '',
    email: '',
  });
  
  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        if (!user) return;
        
        // Initialize with user data
        setProfileData({
          id: user.id,
          full_name: user.user_metadata?.full_name || '',
          email: user.email || '',
          organization: user.user_metadata?.organization,
          water_usage_goal: user.user_metadata?.water_usage_goal,
          avatar_url: user.user_metadata?.avatar_url,
        });
      } catch (error) {
        console.error('Error fetching profile:', error);
        toast({
          title: translate("Error"),
          description: translate("Failed to load profile data"),
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchProfileData();
  }, [user]);

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      const { error } = await updateUserProfile({
        name: profileData.full_name,
        avatar_url: profileData.avatar_url,
      });

      if (error) throw error;

      toast({
        title: translate("Success"),
        description: translate("Profile updated successfully"),
      });
    } catch (error) {
      console.error('Error updating profile:', error);
      toast({
        title: translate("Error"),
        description: translate("Failed to update profile"),
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">
          {translate("Profile Settings")}
        </h1>
        <p className="text-muted-foreground">
          {translate("Manage your account settings and preferences")}
        </p>
            </div>
            
      <div className="grid gap-6">
                  <Card>
                    <CardHeader>
            <CardTitle>{translate("Personal Information")}</CardTitle>
                    </CardHeader>
                    <CardContent>
            <form onSubmit={handleUpdateProfile} className="space-y-6">
              <div className="flex items-center gap-6">
                <Avatar className="h-20 w-20">
                  <AvatarImage src={profileData.avatar_url} />
                  <AvatarFallback>
                    <User className="h-10 w-10" />
                  </AvatarFallback>
                </Avatar>
                <Button variant="outline" type="button">
                  {translate("Change Avatar")}
                </Button>
                              </div>

              <div className="grid gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="full_name">{translate("Full Name")}</Label>
                  <Input
                    id="full_name"
                    value={profileData.full_name}
                    onChange={(e) =>
                      setProfileData({ ...profileData, full_name: e.target.value })
                    }
                  />
                            </div>

                <div className="grid gap-2">
                  <Label htmlFor="email">{translate("Email")}</Label>
                  <Input
                    id="email"
                    type="email"
                    value={profileData.email}
                    disabled
                  />
                        </div>

                <div className="grid gap-2">
                  <Label htmlFor="organization">{translate("Organization")}</Label>
                  <Input
                    id="organization"
                    value={profileData.organization || ''}
                    onChange={(e) =>
                      setProfileData({ ...profileData, organization: e.target.value })
                    }
                    placeholder={translate(
                      mode === 'household'
                        ? "Enter household name"
                        : "Enter farm/organization name"
                    )}
                  />
                                </div>

                <div className="grid gap-2">
                  <Label htmlFor="water_usage_goal">
                    {translate("Water Usage Goal (Liters/day)")}
                  </Label>
                  <Input
                    id="water_usage_goal"
                    type="number"
                    value={profileData.water_usage_goal || ''}
                    onChange={(e) =>
                      setProfileData({
                        ...profileData,
                        water_usage_goal: parseInt(e.target.value),
                      })
                    }
                    placeholder={translate("Set your daily water usage goal")}
                  />
                                </div>
                              </div>

              <Button type="submit" disabled={saving}>
                {saving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {translate("Saving...")}
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    {translate("Save Changes")}
                  </>
                )}
              </Button>
            </form>
                    </CardContent>
                  </Card>
                
                  <Card>
                    <CardHeader>
            <CardTitle>{translate("Account Preferences")}</CardTitle>
                    </CardHeader>
                    <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                        <div>
                  <h3 className="font-medium">{translate("Email Notifications")}</h3>
                  <p className="text-sm text-muted-foreground">
                    {translate("Receive updates about your water usage")}
                  </p>
                          </div>
                <Button variant="outline">
                  {translate("Manage Notifications")}
                </Button>
                        </div>
                        
              <div className="flex items-center justify-between">
                        <div>
                  <h3 className="font-medium">{translate("Connected Devices")}</h3>
                  <p className="text-sm text-muted-foreground">
                    {translate("Manage your connected water meters and sensors")}
                  </p>
                              </div>
                <Button variant="outline">
                  {translate("Manage Devices")}
                </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
        </div>
    </div>
  );
};

export default Profile;
